/**
 * The /looker filter bar's single "Export" button (owner request 2026-10-13):
 * PDF and CSV live in its menu instead of being two buttons. Each item keeps
 * its own readiness rule (PDF waits for the page, CSV also for every box and
 * page-level read); the trigger is disabled only when neither can run.
 *
 * @module components/looker/ExportMenu
 */
import React, { useCallback, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { POPOVER_PANEL_CLASS, useDismiss, usePanelPlacement } from './statusPopover';

const TRIGGER =
  'inline-flex h-9 items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-3 text-sm font-medium text-ink-900 hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-50';
const ITEM =
  'block w-full rounded-md px-3 py-2 text-left text-sm font-medium text-ink-900 hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent';

interface Props {
  onPdf: () => void;
  onCsv: () => void;
  pdfDisabled: boolean;
  csvDisabled: boolean;
}

export const ExportMenu: React.FC<Props> = ({ onPdf, onCsv, pdfDisabled, csvDisabled }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, root, close);
  const placement = usePanelPlacement(open, root);
  const pick = (run: () => void) => () => {
    setOpen(false);
    run();
  };

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        className={TRIGGER}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        disabled={pdfDisabled && csvDisabled}
        onClick={() => setOpen((v) => !v)}
      >
        {t('looker.exportMenu')}
        <ChevronDown className="h-4 w-4" aria-hidden="true" />
      </button>
      {open && (
        <div id={menuId} role="menu" className={`${POPOVER_PANEL_CLASS} p-1`} style={placement ?? undefined}>
          <button type="button" role="menuitem" className={ITEM} disabled={pdfDisabled} onClick={pick(onPdf)}>
            {t('looker.print.export')}
          </button>
          <button type="button" role="menuitem" className={ITEM} disabled={csvDisabled} onClick={pick(onCsv)}>
            {t('looker.exportCsv')}
          </button>
        </div>
      )}
    </div>
  );
};
